import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import {
  ApiResponse,
  FilteredTableComponent,
  FilteredTablePayload,
  Meta,
} from '../../../../common';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { AvatarComponent } from '../../../../../ui-components/lib/avatar/avatar.component';
import { BadgeComponent } from '../../../../../ui-components/lib/badge/badge.component';
import { TruncatePipe } from '../../../../../ui-components/pipes/truncate/truncate.pipe';
import {
  TableColumns,
  TableFilterTab,
} from '../../../../../ui-components/lib/table/table.model';
import { finalize } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActionListComponent } from '../../../../common/components/action-list/action-list.component';
import { ConfirmationPopupComponent } from '../../../../common/components/confirmation-popup/confirmation-popup.component';
import { ActionList } from '../../../../common/models/action-list.model';
import {
  Delegation,
  DelegationStatus,
  DelegationTableRow,
} from './delegation.model';
import {
  COLUMNS,
  DELEGATION_CANCEL_ACTION,
  DELEGATION_COLUMN_IDENTIFIERS,
  DELEGATION_EDIT_ACTION,
  DELEGATION_REVOKE_ACTION,
  DELEGATION_PERSON_COLUMN_HEADER,
  DELEGATION_PERSON_COLUMN_SORT_KEY,
  DELEGATION_STATUS_BADGE_CLASS,
  DELEGATION_TAB_FILTER_OPTIONS,
  DELEGATION_TAB_KEYS,
  DELEGATION_VIEW_DETAILS_ACTION,
  getDelegationRowActions,
  DELEGATION_FILTER_OPTIONS,
} from './delegation.constant';
import { DelegationService } from './delegation.service';
import { ModalServiceService } from '../../../../../ui-components/lib/modal/modal-service.service';
import { ToasterService } from '../../../../../ui-components/services/toast/toast.service';
import { Router, RouterLink } from '@angular/router';
import { EMPLOYEE_ROUTES } from '../../../../../core/common/constants/employee.routing.constants';
import { TenantService } from '../../../../common/services/tenant/tenant.service';
import { AddIconComponent } from '@kuiper/icons';
import { FilterOption } from '../../../../../ui-components/lib/filter-dropdown/filter-dropdown.model';

@Component({
  selector: 'lib-delegation-tab',
  imports: [
    DatePipe,
    FilteredTableComponent,
    TranslocoPipe,
    AvatarComponent,
    BadgeComponent,
    TruncatePipe,
    ActionListComponent,
    ConfirmationPopupComponent,
    RouterLink,
    AddIconComponent,
  ],
  templateUrl: './delegation-tab.component.html',
  styleUrl: './delegation-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DelegationTabComponent {
  protected readonly COLUMN_IDENTIFIERS = DELEGATION_COLUMN_IDENTIFIERS;
  protected readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);
  private readonly modalService = inject(ModalServiceService);
  private readonly toasterService = inject(ToasterService);
  private readonly delegationService = inject(DelegationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly tenantService = inject(TenantService);

  EMPLOYEE_ROUTES = EMPLOYEE_ROUTES;

  /******** pagination ********/
  meta = signal<Meta | undefined>(undefined);

  /*********** table  *********/
  isLoading = signal<boolean>(false);
  dataSource = signal<Delegation[]>([]);
  totalItems = signal<number>(0);

  readonly actionMenuItemsByStatus = computed<
    Record<DelegationStatus, ActionList[]>
  >(() => {
    const isDelegatedToMe = this.isDelegatedToMeTab();

    return Object.values(DelegationStatus).reduce(
      (items, status) => {
        items[status] = getDelegationRowActions(status, isDelegatedToMe).map(
          action => ({
            ...action,
            label: this.transloco.translate(action.label),
          })
        );
        return items;
      },
      {} as Record<DelegationStatus, ActionList[]>
    );
  });

  readonly delegationTableRows = computed<DelegationTableRow[]>(() => {
    const actionMenuItemsByStatus = this.actionMenuItemsByStatus();
    return this.dataSource().map(row => ({
      ...row,
      statusBadgeClass: `${DELEGATION_STATUS_BADGE_CLASS[row.state.value] ?? ''}`,
      actionMenuItems: actionMenuItemsByStatus[row.state.value] ?? [],
    }));
  });

  readonly columns = computed<TableColumns[]>(() => {
    const isDelegatedToMe = this.isDelegatedToMeTab();
    const tabKey = isDelegatedToMe
      ? DELEGATION_TAB_KEYS.DELEGATED_TO_ME
      : DELEGATION_TAB_KEYS.DELEGATED_BY_ME;

    return COLUMNS.map(column => ({
      ...column,
      header: this.transloco.translate(
        column.identifier === DELEGATION_COLUMN_IDENTIFIERS.DELEGATE
          ? DELEGATION_PERSON_COLUMN_HEADER[tabKey]
          : column.header
      ),
      sortKey:
        column.identifier === DELEGATION_COLUMN_IDENTIFIERS.DELEGATE
          ? DELEGATION_PERSON_COLUMN_SORT_KEY[tabKey]
          : column.sortKey,
    }));
  });

  tableFilterTabs = signal<TableFilterTab[]>(
    DELEGATION_TAB_FILTER_OPTIONS.map(option => ({
      ...option,
      label: this.transloco.translate(option.label),
    }))
  );

  filterOptions = signal<FilterOption[]>(
    DELEGATION_FILTER_OPTIONS.map(option => ({
      ...option,
      label: this.transloco.translate(option.label),
      placeholder: this.transloco.translate(option.placeholder ?? ''),
      options: option.options?.map(option => ({
        ...option,
        label: this.transloco.translate(option.label ?? ''),
      })),
    }))
  );

  filterMapping = signal<Record<string, string>>({
    state: 'state',
    dateStart: 'date][from',
    dateEnd: 'date][to',
  });

  payload = signal<FilteredTablePayload>({
    page: 1,
    size: 25,
    sortColumn: {
      key: '',
      value: 'asc',
    },
    filters: {},
    search: '',
    tab: this.tableFilterTabs()[0],
  });

  readonly isDelegatedToMeTab = computed(
    () => this.payload().tab?.key === DELEGATION_TAB_KEYS.DELEGATED_TO_ME
  );

  private readonly cancelModal = viewChild<TemplateRef<unknown>>(
    'cancelDelegationModal'
  );
  private readonly revokeModal = viewChild<TemplateRef<unknown>>(
    'revokeDelegationModal'
  );

  readonly selectedDelegation = signal<Delegation | null>(null);
  readonly isActionLoading = signal(false);

  getDelegationList$ = () => {
    this.isLoading.set(true);
    this.clearTableData();
    const { page, size, sortColumn, filters, search } = this.payload();
    const request$ = this.isDelegatedToMeTab()
      ? this.delegationService.getReceivedDelegations(
          page,
          size,
          sortColumn,
          filters,
          search
        )
      : this.delegationService.getDelegations(
          page,
          size,
          sortColumn,
          filters,
          search
        );

    return request$.pipe(
      finalize(() => this.isLoading.set(false)),
      takeUntilDestroyed(this.destroyRef)
    );
  };

  getDelegationList(payload?: FilteredTablePayload) {
    if (payload) {
      this.payload.set(payload);
    }

    this.getDelegationList$().subscribe({
      next: (res: ApiResponse<Delegation>) => {
        this.handleGetDelegationListSuccess(res);
      },
    });
  }

  private clearTableData(): void {
    this.dataSource.set([]);
    this.totalItems.set(0);
    this.meta.set(undefined);
  }

  handleGetDelegationListSuccess(res: ApiResponse<Delegation>) {
    this.dataSource.set(res.data);
    this.meta.set(res.meta);
    this.totalItems.set(res.meta.total);
  }

  viewDelegationDetails(row: Delegation): void {
    this.router.navigate([
      this.tenantService.tenantPath(
        `${EMPLOYEE_ROUTES.ACCOUNT_SETTINGS.DELEGATION_DETAILS}/${row.id}`
      ),
    ]);
  }

  editDelegation(row: Delegation): void {
    this.router.navigate([
      this.tenantService.tenantPath(
        `${EMPLOYEE_ROUTES.ACCOUNT_SETTINGS.DELEGATION_FORM}/${row.id}`
      ),
    ]);
  }

  onActionClick(actionKey: string, row: Delegation): void {
    if (actionKey === DELEGATION_VIEW_DETAILS_ACTION.key) {
      this.viewDelegationDetails(row);
      return;
    }

    if (actionKey === DELEGATION_EDIT_ACTION.key) {
      this.editDelegation(row);
      return;
    }

    if (actionKey === DELEGATION_CANCEL_ACTION.key) {
      this.openStatusActionModal(row, 'cancel');
      return;
    }

    if (actionKey === DELEGATION_REVOKE_ACTION.key) {
      this.openStatusActionModal(row, 'revoke');
    }
  }

  confirmCancelDelegation = () => {
    this.updateDelegationStatus(
      DelegationStatus.CANCELLED,
      'Canceled',
      'delegation_tab_cancel_success_message'
    );
  };

  confirmRevokeDelegation = () => {
    this.updateDelegationStatus(
      DelegationStatus.REVOKED,
      'Revoked',
      'delegation_tab_revoke_success_message'
    );
  };

  closeModal(): void {
    this.modalService.modalContent.set({ isOpen: false });
    this.selectedDelegation.set(null);
  }

  private openStatusActionModal(
    row: Delegation,
    type: 'cancel' | 'revoke'
  ): void {
    this.selectedDelegation.set(row);
    const customizeContent =
      type === 'cancel' ? this.cancelModal() : this.revokeModal();

    this.modalService.modalContent.set({
      isOpen: true,
      hasCloseIcon: false,
      customizeContent,
      width: '24',
    });
  }

  private updateDelegationStatus(
    status: DelegationStatus,
    label: string,
    successMessageKey: string
  ): void {
    const row = this.selectedDelegation();
    if (!row) {
      return;
    }

    this.isActionLoading.set(true);
    const request$ =
      status === DelegationStatus.CANCELLED
        ? this.cancelDelegation$(row.id)
        : this.revokeDelegation$(row.id);

    request$
      .pipe(
        finalize(() => this.isActionLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.getDelegationList();
          this.toasterService.add(
            this.transloco.translate(successMessageKey),
            '',
            '',
            'success',
            true,
            true
          );
          this.closeModal();
        },
        error: (error: { message?: string }) => {
          this.toasterService.add(
            error.message ?? '',
            '',
            '',
            'error',
            true,
            true
          );
        },
      });
  }

  cancelDelegation$(id: number) {
    return this.delegationService.cancelDelegation(id.toString());
  }

  revokeDelegation$(id: number) {
    return this.delegationService.revokeDelegation(id.toString());
  }
}
